import { Component, computed, inject, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BarChartComponent } from "../widgets/bar-chart/bar-chart.component";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { GatePositionComponent } from "../../../../shared/widgets/gate-position/gate-position.component";
import { ExpectedEnergyLossComponent } from "../../../../shared/widgets/expected-energy-loss/expected-energy-loss.component";
import { OpenDurationComponent } from "../../../../shared/widgets/open-duration/open-duration.component";
import { LoadChangesComponent } from "../../../../shared/widgets/load-changes/load-changes.component";
import { TemperatureDisplayComponent } from "../../../../shared/widgets/temperature-display/temperature-display.component";
import { WeatherReportComponent } from "../../../../shared/widgets/weather-report/weather-report.component";
import { LineChartComponent } from "../widgets/line-chart/line-chart.component";
import { TranslateModule } from "@ngx-translate/core";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";

import { ChartChangeTimeEvent } from "../../../../models/ui/chartData";
import { SimplebarAngularModule } from "simplebar-angular";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GateSystemEnergyToolService } from "./facade/gate-system-energy-tool.service";
import { GateIdentifierPipe } from "../../../../shared/pipes/gateIdentifier/gate-identifier.pipe";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { GateStatusStateService } from "../../../../services/state/gate-status/gate-status-state.service";

@Component({
  standalone: true,
  selector: "app-gate-system-energy",
  imports: [
    BarChartComponent,
    GatePositionComponent,
    ExpectedEnergyLossComponent,
    OpenDurationComponent,
    LoadChangesComponent,
    TemperatureDisplayComponent,
    WeatherReportComponent,
    LineChartComponent,
    TranslateModule,
    SvgIconComponent,
    SimplebarAngularModule,
    GateIdentifierPipe,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./gate-system-energy.component.html",
  styleUrl: "./gate-system-energy.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [GateSystemEnergyToolService, GateStatusStateService],
})
export class GateSystemEnergyComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  public sourceId!: string;
  public sourceType = ASSET_TYPES.GATE_SYSTEM;
  public permissions = inject(PermissionService);

  public energyToolFacade = inject(GateSystemEnergyToolService);
  public gateSystemRefLoading = computed(() => this.energyToolFacade.gateSystemRef() === null);
  public timeOut = createTimeoutSignal(this.gateSystemRefLoading);

  constructor() {
    this.route.url.pipe(takeUntilDestroyed()).subscribe((url) => {
      this.sourceId = url[1].path;
      this.energyToolFacade.loadInitialState(this.sourceId);
    });
  }

  public ngOnDestroy(): void {
    this.energyToolFacade.destroy();
  }

  /**
   * Handler for time range change events emitted by the expected energy loss widget.
   * @param timeWindow The new time range chosen by the user.
   */
  public onChangeTimeExpecteEnergyLoss(timeWindow: string) {
    this.energyToolFacade.updateEnergyLossForTimeWindow(this.sourceId, timeWindow);
  }

  /**
   * Handler for time range change events emitted by the cycle widget.
   * @param changeEvent The new time range chosen by the user.
   */
  public onChangeTimeCycle(changeEvent: string) {
    this.energyToolFacade.updateCycleCountForTimeWindow(this.sourceId, changeEvent);
  }

  /**
   * Handler for time range change events emitted by the bar chart.
   * @param changeEvent The new time range chosen by the user.
   */
  public onChangeTimeBarChart(changeEvent: ChartChangeTimeEvent) {
    this.energyToolFacade.updateBarChartForTimeRange(
      changeEvent.start,
      changeEvent.end,
      changeEvent.timeFrame,
      this.sourceId,
      changeEvent.assetLabels?.[0]
    );
  }

  /**
   * Handler for time range change events emitted by the line chart.
   * @param changeEvent The new time range chosen by the user.
   */
  public onChangeTimeLineChart(changeEvent: ChartChangeTimeEvent) {
    this.energyToolFacade.updateLineChartForTimeRange(
      changeEvent.start,
      changeEvent.end,
      changeEvent.timeFrame,
      this.sourceId
    );
  }
}
